/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Footer from '../components/Footer';
import BulkUploadModal from '../components/BulkUploadModal'; // Import the modal component

function Lead() {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false); // Modal state

    // React Table State (like Countries component)
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Advanced filters (keep existing filter functionality)
    const [filters, setFilters] = useState({
        company_name: '',
        sub_industry: '',
        phone: '',
        whatsapp_no: ''
    });

    // "Has Quotation" filter
    const [filterQuoted,  setFilterQuoted]  = useState(false);
    const [quotedLeadIds, setQuotedLeadIds] = useState(new Set());

    // Filter leads based on search term (unified search like Countries)
    const filteredLeads = leads.filter(lead => {
        if (filterQuoted && !quotedLeadIds.has(lead.id)) return false;
        return (
            lead.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.sub_industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.whatsapp_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.status?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    // Apply advanced filters if any are set
    const advancedFilteredLeads = filteredLeads.filter(lead => {
        return (
            (filters.company_name === '' ||
                lead.company_name?.toLowerCase().includes(filters.company_name.toLowerCase())) &&
            (filters.sub_industry === '' ||
                lead.sub_industry?.toLowerCase().includes(filters.sub_industry.toLowerCase())) &&
            (filters.phone === '' ||
                lead.phone?.includes(filters.phone)) &&
            (filters.whatsapp_no === '' ||
                lead.whatsapp_no?.includes(filters.whatsapp_no))
        );
    });

    // Sort filtered leads (same logic as Countries)
    const sortedLeads = React.useMemo(() => {
        let sortableLeads = [...advancedFilteredLeads];
        if (sortConfig.key) {
            sortableLeads.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableLeads;
    }, [advancedFilteredLeads, sortConfig]);

    // Calculate pagination (same logic as Countries)
    const totalItems = sortedLeads.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentLeads = sortedLeads.slice(startIndex, endIndex);

    // Handle sorting (same as Countries)
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Get sort icon (same as Countries)
    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? '↑' : '↓';
        }
        return '';
    };

    // Reset pagination when search term changes (same as Countries)
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    // Fetch Leads from API + quoted lead IDs in parallel
    const fetchLeads = async () => {
        try {
            setLoading(true);
            const [leadsRes, quotsRes] = await Promise.all([
                axiosInstance.get("/leads/"),
                axiosInstance.get("/quotations?type=quotation").catch(() => ({ data: { data: [] } })),
            ]);
            setLeads(leadsRes.data.data || leadsRes.data);

            // Build a Set of lead_ids that have at least one quotation
            const docs = quotsRes.data.data || [];
            setQuotedLeadIds(new Set(docs.filter(q => q.lead_id).map(q => q.lead_id)));
        } catch (error) {
            console.error("Error fetching Leads:", error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to fetch leads data'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeads();
    }, []);

    // Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilterQuoted(false);
        setFilters({
            company_name: '',
            sub_industry: '',
            phone: '',
            whatsapp_no: ''
        });
        setSearchTerm(''); // Also clear the main search term
    };

    // Navigate to Lead Details page
    const handleViewDetails = (leadId) => {
        navigate(`/lead-details/${leadId}`);
    };

    // Edit Lead - Navigate to add-lead page with lead data for update
    const handleEdit = (lead) => {
        console.log("lead data:", lead)
        navigate('/add-lead', { state: { lead, isEdit: true } });
    };

    // Delete Lead
    const handleDelete = async (leadId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`/leads/${leadId}`);
                Swal.fire('Deleted!', 'Lead has been deleted.', 'success');
                fetchLeads(); // Refresh the list
            } catch (error) {
                console.error('Error deleting lead:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Failed to delete lead'
                });
            }
        }
    };

    // Handle bulk upload success
    const handleBulkUploadSuccess = () => {
        fetchLeads(); // Refresh the leads list after successful upload
    };

    // Pagination component (same as Countries)
    const Pagination = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                    {filteredLeads.length !== leads.length && ` (filtered from ${leads.length} total entries)`}
                </div>
                <div className="d-flex align-items-center">
                    <nav>
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> First
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <i className="fa fa-angle-double-left me-2"></i> Previous
                                </button>
                            </li>
                            {pageNumbers.map(number => (
                                <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(number)}
                                    >
                                        {number}
                                    </button>
                                    </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next <i className="fa fa-angle-double-right ms-2"></i>
                                </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                >
                                    Last
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="content-page-header">
                            <h5>Leads</h5>
                            <div className="list-btn">
                                <ul className="filter-list">
                                    <li>
                                        <button
                                            className="btn btn-filters w-auto"
                                            onClick={() => $('#filter_inputs').toggle()}
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="bottom"
                                            title="Filter"
                                        >
                                            <span className="me-2">
                                                <img src="assets/img/icons/filter-icon.svg" alt="filter" />
                                            </span>Filter
                                        </button>
                                    </li>
                                    <li>
                                        <div className="dropdown dropdown-action" data-bs-toggle="tooltip" data-bs-placement="top" title="Download">
                                            <a href="#" className="btn-filters" data-bs-toggle="dropdown" aria-expanded="false">
                                                <span><i className="fe fe-download"></i></span>
                                            </a>
                                            <div className="dropdown-menu dropdown-menu-end">
                                                <ul className="d-block">
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-pdf me-2"></i>PDF
                                                        </a>
                                                    </li>
                                                    <li>
                                                        <a className="d-flex align-items-center download-item" href="javascript:void(0);">
                                                            <i className="far fa-file-text me-2"></i>CSV
                                                        </a>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                    <li>
                                        <a className="btn-filters" href="javascript:void(0);" data-bs-toggle="tooltip" data-bs-placement="bottom" title="Print">
                                            <span><i className="fe fe-printer"></i></span>
                                        </a>
                                    </li>
                                    <li>
                                        <button 
                                            className="btn btn-import" 
                                            onClick={() => setShowBulkUploadModal(true)}
                                        >
                                            <span><i className="fe fe-check-square me-2"></i>Import Leads</span>
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            className={`btn ${filterQuoted ? 'btn-warning' : 'btn-outline-warning'} me-2`}
                                            onClick={() => { setFilterQuoted(v => !v); setCurrentPage(1); }}
                                            title={filterQuoted ? 'Showing leads with quotation only — click to show all' : 'Filter: show only leads that have a quotation'}
                                        >
                                            <i className="fas fa-file-alt me-1"></i>
                                            {filterQuoted
                                                ? `Quoted (${quotedLeadIds.size})`
                                                : 'Has Quotation'}
                                        </button>
                                    </li>
                                    <li>
                                        <a className="btn btn-primary" href="/add-lead">
                                            <i className="fa fa-plus-circle me-2" aria-hidden="true"></i>Add Lead
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    {/* /Page Header */}

                    {/* Search Filter */}
                    <div id="filter_inputs" className="card filter-card" style={{ display: 'none' }}>
                        <div className="card-body pb-0">
                            <div className="row">
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Lead Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            placeholder="Search by Lead name"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Sub Industry</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="sub_industry"
                                            value={filters.sub_industry}
                                            onChange={handleFilterChange}
                                            placeholder="Search by sub industry"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>Phone</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone"
                                            value={filters.phone}
                                            onChange={handleFilterChange}
                                            placeholder="Search by phone"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <div className="input-block mb-3">
                                        <label>WhatsApp</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="whatsapp_no"
                                            value={filters.whatsapp_no}
                                            onChange={handleFilterChange}
                                            placeholder="Search by WhatsApp"
                                        />
                                    </div>
                                </div>
                                <div className="col-sm-12">
                                    <div className="input-block mb-3 text-end">
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={clearFilters}
                                        >
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Search Filter */}

                    {/* Table */}
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card-table">
                                <div className="card-body">
                                    {/* Table Controls (same as Countries) */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="d-flex align-items-center">
                                            <label className="me-2">Show</label>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={(e) => {
                                                    setItemsPerPage(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <label className="ms-2">entries</label>
                                        </div>
                                        <div>
                                            <label className="me-2">Search:</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm d-inline-block"
                                                style={{ width: '200px' }}
                                                placeholder="Search leads..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-center table-hover">
                                            <thead className="thead-light">
                                                <tr>
                                                    <th>#</th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('company_name')}
                                                    >
                                                        Lead Name {getSortIcon('company_name')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('phone')}
                                                    >
                                                        Phone {getSortIcon('phone')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('whatsapp_no')}
                                                    >
                                                        WhatsApp {getSortIcon('whatsapp_no')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('sub_industry')}
                                                    >
                                                        Sub Industry {getSortIcon('sub_industry')}
                                                    </th>
                                                    <th
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleSort('status')}
                                                    >
                                                        Status {getSortIcon('status')}
                                                    </th>
                                                    <th className="no-sort">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">
                                                            <div className="spinner-border" role="status">
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : currentLeads.length > 0 ? (
                                                    currentLeads.map((lead, index) => (
                                                        <tr key={lead.id}>
                                                            <td>{startIndex + index + 1}</td>
                                                            <td>
                                                                <span 
                                                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                                                    onClick={() => handleViewDetails(lead.id)}
                                                                    title="Click to view details"
                                                                >
                                                                    {lead.company_name}
                                                                </span>
                                                            </td>
                                                            <td>{lead.phone}</td>
                                                            <td>{lead.whatsapp_no}</td>
                                                            <td>{lead.sub_industry}</td>
                                                            <td>
                                                                <span className={`badge ${lead.status === 'New' ? 'bg-primary' :
                                                                        lead.status === 'Pitching' ? 'bg-info' :
                                                                            lead.status === 'Profile Shared' ? 'bg-secondary' :
                                                                                lead.status === 'Secure Lead' ? 'bg-success' :
                                                                                    lead.status === 'Proposal Sent' ? 'bg-warning' :
                                                                                        lead.status === 'Won' ? 'bg-success' :
                                                                                            lead.status === 'Lost' ? 'bg-danger' :
                                                                                                'bg-light text-dark'
                                                                    }`}>
                                                                    {lead.status}
                                                                </span>
                                                            </td>
                                                            <td className="d-flex align-items-center">
                                                                <a className="btn-action-icon bg-primary-light me-2"
                                                                    onClick={() => handleViewDetails(lead.id)}
                                                                    title="View Details"
                                                                >
                                                                    <i className="fa fa-eye me-1"></i>
                                                                </a>
                                                                <a className="btn-action-icon bg-success-light me-2"
                                                                    onClick={() => handleEdit(lead)}
                                                                >
                                                                    <i className="fa fa-edit me-1"></i>
                                                                </a>
                                                                <a className="btn-action-icon btn-greys bg-danger-light"
                                                                    onClick={() => handleDelete(lead.id)}
                                                                >
                                                                    <i className="fa fa-trash-alt me-1"></i>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">
                                                            {searchTerm ? "No matching leads found" : "No leads available in table"}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination (same as Countries) */}
                                    {totalItems > 0 && <Pagination />}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Table */}
                </div>
                <Footer />
            </div>

            {/* Bulk Upload Modal */}
            <BulkUploadModal
                isOpen={showBulkUploadModal}
                onClose={() => setShowBulkUploadModal(false)}
                onUploadSuccess={handleBulkUploadSuccess}
            />
        </div>
    )
}

export default Lead;